<template>
	<v-card class="d-flex flex-column pb-5">
		<v-toolbar color="primary" dark flat>
			<v-toolbar-title style="flex: 0 1 auto">{{ title }}</v-toolbar-title>

			<v-text-field
				v-model="searchKey"
				prepend-inner-icon="mdi-magnify"
				placeholder="Search roles..."
				density="compact"
				variant="solo"
				flat
				rounded
				hide-details
				clearable
				bg-color="white"
				class="flex-grow-0 ml-4 role-toolbar-search"
			/>

			<v-spacer />

			<v-btn
				class="mr-3 text-primary bg-grey"
				elevation="2"
				@click="displayDeleted = !displayDeleted"
			>
				<template v-if="!displayDeleted">
					<v-icon start> mdi-eye </v-icon>

					Show deleted
				</template>

				<template v-else>
					<v-icon start> mdi-eye-off </v-icon>

					Hide deleted
				</template>
			</v-btn>

			<v-dialog
				v-model="dialog"
				max-width="800px"
				@click:outside="clearSelectedRole"
			>
				<template #activator="{ props }">
					<v-btn v-bind="props" class="bg-secondary mr-3">
						<v-icon start> mdi-plus </v-icon>

						Add new role
					</v-btn>
				</template>

				<role-edit
					:default-role="selectedRole"
					@cancel="closeDialog"
					@refresh="closeDialogAndRefresh"
				/>
			</v-dialog>

			<v-dialog v-model="deleteDialog" max-width="800px">
				<v-card>
					<v-toolbar color="primary" dark flat>
						<v-toolbar-title>Delete role</v-toolbar-title>
					</v-toolbar>

					<v-card-title class="text-center pb-4">
						Are you sure you want to delete this role?
					</v-card-title>

					<v-divider />

					<v-card-subtitle class="text-center pt-5 pb-5">
						{{ getRoleLabel(selectedRole) }}
					</v-card-subtitle>

					<v-divider />

					<v-card-actions class="py-5">
						<v-spacer />

						<v-btn
							class="bg-secondary"
							elevation="2"
							@click="deleteRole"
						>
							Yes
						</v-btn>

						<v-btn class="bg-grey" elevation="2" @click="closeDeleteDialog">
							No
						</v-btn>

						<v-spacer />
					</v-card-actions>
				</v-card>
			</v-dialog>
			<v-dialog v-model="restoreDialog" max-width="800px">
				<v-card>
					<v-toolbar color="primary" dark flat>
						<v-toolbar-title>Restore role</v-toolbar-title>
					</v-toolbar>

					<v-card-title class="text-center pb-4">
						Are you sure you want to restore this role?
					</v-card-title>

					<v-divider />

					<v-card-subtitle class="text-center pt-5 pb-5">
						{{ selectedRole.name }}
					</v-card-subtitle>

					<v-divider />

					<v-card-actions class="py-5">
						<v-spacer />
						<v-btn
							class="bg-secondary"
							elevation="2"
							@click="restoreRole"
						>
							Yes
						</v-btn>
						<v-btn class="bg-grey" elevation="2" @click="closeRestoreDialog">
							No
						</v-btn>
						<v-spacer />
					</v-card-actions>
				</v-card>
			</v-dialog>
		</v-toolbar>

		<v-data-table-server
			v-model:options="options"
			class="role-table"
			item-key="id"
			:headers="headers"
			:items="roles"
			:row-props="getRowProps"
			:loading="loading"
			:items-length="totalRoles"
		>
			<template #item.id="{ item }">
				{{ item.id }}
			</template>

			<template #item.description="{ item }">
				<div v-overflow-title class="description-clamp">
					{{ item.description }}
				</div>
			</template>

			<template #item.created_at="{ item }">
				{{ getCreatedTimestamp(item) }}
			</template>

			<template #item.permission_count="{ item }">
				{{ getPermissionCount(item) }}
			</template>

			<template #item.actions="{ item }">
				<v-tooltip v-if="!item.deleted_at" location="bottom" open-delay="500">
					<template #activator="{ props }">
						<v-hover v-slot="{ hover }">
							<v-icon
								v-bind="props"
								class="mr-2"
								:color="hover ? 'secondary' : ''"
								@click="edit(item)"
							>
								mdi-pencil
							</v-icon>
						</v-hover>
					</template>

					<span>Edit</span>
				</v-tooltip>

				<v-tooltip v-if="!item.deleted_at" location="bottom" open-delay="500">
					<template #activator="{ props }">
						<v-hover v-slot="{ hover }">
							<v-icon
								v-bind="props"
								:color="hover ? 'error' : ''"
								@click="remove(item)"
							>
								mdi-delete
							</v-icon>
						</v-hover>
					</template>

					<span>Delete</span>
				</v-tooltip>
				<v-tooltip v-if="item.deleted_at" location="bottom" open-delay="500">
					<template #activator="{ props }">
						<v-hover v-slot="{ hover }">
							<v-icon
								v-bind="props"
								:color="hover ? 'secondary' : ''"
								@click="restore(item)"
							>
								mdi-restore
							</v-icon>
						</v-hover>
					</template>

					<span>Restore</span>
				</v-tooltip>
			</template>
		</v-data-table-server>
	</v-card>
</template>

<script>
import _ from "lodash";

import { mapActions } from "pinia";
import { useMainStore } from "@/store";

import RoleEdit from "@/pages/UserManager/RoleEdit";
import overflowTitle from "@/directives/overflowTitle";

export default {
	name: "RoleTable",

	components: {
		RoleEdit,
	},
	directives: { overflowTitle },
	emits: ["refresh-all"],

	data() {
		return {
			deleteDialog: false,
			restoreDialog: false,
			dialog: false,
			displayDeleted: false,
			loading: false,
			options: {},
			searchKey: "",
			selectedRole: {},
			title: "Roles",
			totalRoles: 0,
			roles: [],
			headers: [
				{
					title: "ID",
					value: "id",
					width: "10%",
				},
				{
					title: "Name",
					value: "name",
					width: "20%",
				},
				{
					title: "Description",
					value: "description",
					align: "left",
					width: "25%",
				},
				{
					title: "Created at",
					value: "created_at",
					align: "center",
					divider: true,
					width: "200px",
				},
				{
					title: "Actions",
					value: "actions",
					align: "center",
					width: "95px",
					sortable: false,
				},
			],
		};
	},

	watch: {
		options: {
			deep: true,
			handler() {
				this.getRoles();
			},
		},
		displayDeleted() {
			this.options.page = 1;
			this.getRoles();
		},
		searchKey() {
			this.debouncedRefetch();
		},
	},

	methods: {
		...mapActions(useMainStore, ["showNotification"]),
		// Debounced so typing doesn't fire a request per keystroke; resets to
		// page 1 then refetches (mirrors the displayDeleted watcher).
		debouncedRefetch: _.debounce(function () {
			this.options.page = 1;
			this.getRoles();
		}, 300),
		getRoleLabel(role) {
			return `${role.name} [${role.slug}]`;
		},
		getPermissionCount(role) {
			return role.permissions.length;
		},
		getCreatedTimestamp(role) {
			return new Date(role.created_at).toLocaleString();
		},
		clearSelectedRole() {
			setTimeout(() => {
				this.selectedRole = {};
			}, 300);
		},
		getRowProps(role) {
			return {
				class: this.isDeleted(role),
			};
		},
		isDeleted(role) {
			return role.deleted_at ? "deleted" : "";
		},
		remove(item) {
			this.selectedRole = Object.assign({}, item);

			this.deleteDialog = true;
		},
		restore(item) {
			this.selectedRole = Object.assign({}, item);

			this.restoreDialog = true;
		},
		closeDeleteDialog() {
			this.deleteDialog = false;

			this.clearSelectedRole();
		},
		deleteRole() {
			this.loading = "secondary";

			if (this.selectedRole) {
				this.$http
					.delete(`/roles/${this.selectedRole.id}`)
					.then((response) => {
						// display success
						this.showNotification(response.data);

						// refresh table
						this.$emit("refresh-all");
					})
					.catch((error) => {
						// display error
						if (error.response) {
							if (error.response.data.message) {
								this.showNotification(error.response.data);
							} else {
								this.showNotification(error.response.data.error);
							}
						}

						this.loading = false;
					});
			} else {
				this.loading = false;
			}

			this.closeDeleteDialog();
		},
		restoreRole() {
			this.loading = "secondary";

			if (this.selectedRole) {
				this.$http
					.put(`/roles/${this.selectedRole.id}`, {
						deletedAt: null,
					})
					.then((response) => {
						this.showNotification(response.data);
						this.$emit("refresh-all");
					})
					.catch((error) => {
						if (error.response) {
							if (error.response.data.message) {
								this.showNotification(error.response.data);
							} else {
								this.showNotification(error.response.data.error);
							}
						}

						this.loading = false;
					});
			} else {
				this.loading = false;
			}

			this.closeRestoreDialog();
		},
		edit(item) {
			this.selectedRole = Object.assign({}, item);

			this.dialog = true;
		},
		closeDialog() {
			this.dialog = false;

			this.clearSelectedRole();
		},
		closeRestoreDialog() {
			this.restoreDialog = false;

			this.clearSelectedRole();
		},
		closeDialogAndRefresh() {
			this.closeDialog();

			this.$nextTick(() => {
				this.$emit("refresh-all");
			});
		},
		getRoles() {
			this.loading = "secondary";

			const { itemsPerPage, page, sortBy, sortDesc } = this.options;

			this.$http
				.get("/roles", {
					params: {
						...(this.displayDeleted ? { deletedOnly: true } : {}),
						...(!this.displayDeleted ? { omitDeleted: true } : {}),
						filter: this.searchKey || undefined,
						itemsPerPage: itemsPerPage,
						page: page,
						sortBy: sortBy,
						sortDesc: sortDesc,
					},
				})
				.then((response) => {
					this.roles = response.data.roles.data;
					this.totalRoles = parseInt(response.data.roles.meta.total, 10);
				})
				.catch((error) => {
					// display error
					if (error.response) {
						if (error.response.data.message) {
							this.showNotification(error.response.data);
						} else {
							this.showNotification(error.response.data.error);
						}
					}
				})
				.finally(() => {
					this.loading = false;
				});
		},
		refresh() {
			this.getRoles();
		},
	},
};
</script>

<style scoped lang="scss">
:deep(.role-table) {
	&.v-data-table .v-table__wrapper td {
		font-size: 12px;
	}

	&.v-data-table .v-table__wrapper th {
		font-size: 12px;
		font-weight: 600;
		color: rgba(0, 0, 0, 0.6);
		height: 48px;
	}
}

.role-toolbar-search {
	min-width: 200px;
	max-width: 300px;
}

// Two lines in the table; the full text shows on hover and in Access levels.
.description-clamp {
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	word-break: break-word;
}
</style>
